import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  LogOut, LayoutDashboard, Inbox, CarFront, FileText,
  KeyRound, Users, Settings, Menu, X, ExternalLink,
} from "lucide-react";
import { useAuth } from "../../auth/AuthContext";
import OverviewSection from "./sections/OverviewSection";
import QuotesSection from "./sections/QuotesSection";
import VehiclesSection from "./sections/VehiclesSection";
import RentalsSection from "./sections/RentalsSection";
import CustomersSection from "./sections/CustomersSection";
import BlogSection from "./sections/BlogSection";
import SettingsSection from "./sections/SettingsSection";

const MENU = [
  { id: "genel", label: "Genel Bakış", icon: LayoutDashboard, component: OverviewSection },
  { id: "teklifler", label: "Teklifler", icon: Inbox, component: QuotesSection },
  { id: "araclar", label: "Araçlar", icon: CarFront, component: VehiclesSection },
  { id: "kiralamalar", label: "Kiralamalar", icon: KeyRound, component: RentalsSection },
  { id: "musteriler", label: "Müşteriler", icon: Users, component: CustomersSection },
  { id: "blog", label: "Blog", icon: FileText, component: BlogSection },
  { id: "ayarlar", label: "Site Ayarları", icon: Settings, component: SettingsSection },
];

export default function AdminPanel() {
  const { user, isAdmin, loading, signOut } = useAuth();
  const navigate = useNavigate();
  const [active, setActive] = useState("genel");
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    if (!loading && !user) navigate("/admin/giris");
  }, [loading, user, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F2F3EB] flex items-center justify-center text-[#6B6B6B] text-sm" data-testid="admin-loading">
        Yükleniyor...
      </div>
    );
  }

  if (!user) return null;

  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-[#F2F3EB] flex items-center justify-center px-6" data-testid="admin-unauthorized">
        <div className="max-w-sm text-center bg-white border border-[#B09060]/20 rounded-sm p-8 shadow-[0_4px_20px_rgba(176,144,96,0.08)]">
          <h1 className="font-serif-display text-xl text-[#404040]">Yetkisiz Erişim</h1>
          <p className="text-sm text-[#6B6B6B] mt-2">
            Bu hesap yönetici yetkisine sahip değil. Supabase üzerinden
            admin_users tablosuna kullanıcınızı ekleyin.
          </p>
          <button
            onClick={signOut}
            data-testid="admin-unauthorized-logout"
            className="mt-5 px-6 py-2.5 rounded-sm border border-[#B09060]/30 text-[#404040] text-xs font-semibold uppercase tracking-wider hover:bg-[#F2F3EB] transition-colors"
          >
            Çıkış Yap
          </button>
        </div>
      </div>
    );
  }

  const ActiveComponent = MENU.find((m) => m.id === active)?.component || OverviewSection;

  return (
    <div className="min-h-screen bg-[#F2F3EB]" data-testid="admin-panel">
      {/* Üst bar */}
      <header className="sticky top-0 z-40 bg-white/90 backdrop-blur border-b border-[#B09060]/15 px-5 py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              className="lg:hidden text-[#404040] p-1"
              onClick={() => setMenuOpen(!menuOpen)}
              data-testid="admin-menu-toggle"
              aria-label="Menü"
            >
              {menuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
            <img src="/assets/logo-monogram.svg" alt="VIA PRIME" className="w-8 h-8 object-contain" />
            <div>
              <div className="font-serif-display text-base font-semibold text-[#404040] leading-tight">VIA PRIME Yönetim</div>
              <div className="text-[10px] text-[#9B9B8F]">{user.email}</div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <a
              href="/"
              className="hidden sm:inline-flex items-center gap-1.5 text-xs text-[#6B6B6B] hover:text-[#8F7648] transition-colors"
              data-testid="admin-view-site"
            >
              <ExternalLink className="w-3.5 h-3.5" /> Siteyi Görüntüle
            </a>
            <button
              onClick={signOut}
              data-testid="admin-logout-button"
              className="flex items-center gap-2 px-4 py-2 rounded-sm border border-[#B09060]/30 text-[#404040] text-xs font-semibold uppercase tracking-wider hover:bg-[#F2F3EB] transition-colors"
            >
              <LogOut className="w-3.5 h-3.5" /> Çıkış
            </button>
          </div>
        </div>
      </header>

      <div className="flex">
        {/* Kenar menü */}
        <aside
          className={`${
            menuOpen ? "block" : "hidden"
          } lg:block fixed lg:sticky top-[57px] z-30 w-60 shrink-0 bg-white border-r border-[#B09060]/15 min-h-[calc(100vh-57px)] p-4`}
          data-testid="admin-sidebar"
        >
          <nav className="space-y-1">
            {MENU.map((m) => (
              <button
                key={m.id}
                onClick={() => { setActive(m.id); setMenuOpen(false); }}
                data-testid={`admin-menu-${m.id}`}
                className={`w-full flex items-center gap-3 px-4 py-2.5 rounded-sm text-sm transition-colors ${
                  active === m.id
                    ? "bg-[#B09060]/10 text-[#8F7648] font-semibold border-l-2 border-[#B09060]"
                    : "text-[#6B6B6B] hover:bg-[#FAFBF6] hover:text-[#404040]"
                }`}
              >
                <m.icon className="w-4 h-4" strokeWidth={1.5} />
                {m.label}
              </button>
            ))}
          </nav>
        </aside>

        {/* İçerik */}
        <main className="flex-1 p-5 lg:p-8 min-w-0" data-testid="admin-content">
          <ActiveComponent />
        </main>
      </div>
    </div>
  );
}
